import { useRenderer } from '@fox/atoms/hooks/use-renderer';
import { tryParseJSONObject } from '@fox/atoms/utils/string';
import { useSubscribeTopicsWithHandler } from '@fox/extensions/src/extension-hooks';
import { ConditionalBlockMeta } from './blocks/form-block/conditionals/contracts';
import { ISection } from './blocks/section-block/contracts';
import { IField } from './form-root/contracts';
import { useFormRuntimeContext } from './form-runtime-context';

/**
 * Recursively collects all question IDs from conditional definitions using sections.
 */
function collectConditionalDependencies(field: IField, sections: ISection[], visited = new Set<string>()): string[] {
	const meta = tryParseJSONObject<ConditionalBlockMeta>(field?.metadataJson);
	if (!meta?.conditionalDefinitions) return [];
	const defs = Array.isArray(meta.conditionalDefinitions) ? meta.conditionalDefinitions : [meta.conditionalDefinitions];

	let ids: string[] = [];
	for (const def of defs) {
		if (def?.questionId && !visited.has(def.questionId)) {
			visited.add(def.questionId);
			ids.push(def.questionId);
			// Find the field in all sections
			const depField = sections.flatMap(s => s.fields).find(f => f.name === def.questionId);
			if (depField) {
				ids = [...ids, ...collectConditionalDependencies(depField, sections, visited)];
			}
		}
	}
	return ids;
}

/**
 * @param dependencies - the dependencies that will trigger a re-render of the form block. Should always include the name of the block in question. Include for example questions that add points for the points block.
 * @param fieldProps - the field props, containing metadataJson for conditional logic.
 */
export function useFormBlockReRenderer(dependencies: string[], fieldProps: IField) {
	const { sections } = useFormRuntimeContext();
	const render = useRenderer();

	let topics = dependencies.map(d => `form-block-${d}`);

	const uniqueDependencies = new Set<string>(topics);
	const fields = sections.flatMap(s => s.fields);
	const filteredFields = fields.filter(f => dependencies.includes(f.name));
	// Ensure we collect all conditional dependencies from any dependency we have
	for (const field of filteredFields) {
		const meta = tryParseJSONObject<ConditionalBlockMeta>(field.metadataJson, {});
		if (meta.conditionalDefinitions) {
			const conditionalIds = collectConditionalDependencies(field, sections);
			conditionalIds.forEach(id => uniqueDependencies.add(`form-block-${id}`));
		}
	}

	// Recursively collect all conditional dependencies using sections for the given block fieldProps
	// In case this block we are checking hasn't been included in dependencies
	const conditionalIds = collectConditionalDependencies(fieldProps, sections);
	conditionalIds.forEach(id => uniqueDependencies.add(`form-block-${id}`));

	topics = Array.from(uniqueDependencies);

	useSubscribeTopicsWithHandler(topics, () => {
		render();
	});
}
